import React, { useState, useCallback, useEffect, useMemo } from "react";
import TableFilters from "../../../common/TableFilters";
import type { ColumnSelection } from "../../../common/TableFilters";
import type { SortButtonChoice } from "@shopify/polaris";
import type { AppliedFilterInterface as PolarisAppliedFilterInterface } from "@shopify/polaris/build/ts/src/types";
import { Select, IndexFiltersMode, ChoiceList } from "@shopify/polaris";
import { getCurrencySymbol, isEmpty, softAssertNumber } from "../../../../helper_functions/utils";
import {
  useUser,
  useHorseVariantVendorsOptions,
  allSuppliers,
  allTypes,
  allVendors,
  useSuppliersSearchOptions,
  useHorseVariantsProductTypeOptions,
  useShopifyChannelAvailabilityOptions,
  useHorseLocationsOptions,
  getNumberParamValue,
  getParamValue,
  getArrayParamValue,
  processQueryParams,
} from "../../../../api_utils/requests";
import TagCheckList from "../../../common/Filters/TagCheckList";
import MoreLessFilter from "../../../common/Filters/MoreLessFilter";
import type {
  DecimalRangeFilter,
  HorseVariantsQueryParams,
  NumberRangeFilter,
  Option,
} from "../../../../api_utils/types";
import { HORSE_VARIANTS_COLLECTIONS } from "../../../../api_utils/types";
import { moreThanLessThanLabel } from "../../../common/Filters/label_tools";
import { CollectionSelect } from "../../../common/Filters/CollectionSelect";

const sortOptions = [
  { label: "Title", directionLabel: "A-Z", value: "title asc" },
  { label: "Title", directionLabel: "Z-A", value: "title desc" },
  { label: "Date", directionLabel: "Oldest/Newest", value: "created_at asc" },
  { label: "Date", directionLabel: "Newest/Oldest", value: "created_at desc" },
  { label: "Variant title", directionLabel: "A-Z", value: "variant_title asc" },
  { label: "Variant title", directionLabel: "Z-A", value: "variant_title desc" },
  { label: "SKU", directionLabel: "A-Z", value: "sku asc" },
  { label: "SKU", directionLabel: "Z-A", value: "sku desc" },
  { label: "Vendor", directionLabel: "A-Z", value: "vendor asc" },
  { label: "Vendor", directionLabel: "Z-A", value: "vendor desc" },
  { label: "Type", directionLabel: "A-Z", value: "product_type asc" },
  { label: "Type", directionLabel: "Z-A", value: "product_type desc" },
  { label: "Available", directionLabel: "Low/High", value: "currently_available_quantity asc" },
  { label: "Available", directionLabel: "High/Low", value: "currently_available_quantity desc" },
  { label: "Weight", directionLabel: "Low/High", value: "weight asc" },
  { label: "Weight", directionLabel: "High/Low", value: "weight desc" },
  { label: "Cost", directionLabel: "Low/High", value: "cost asc" },
  { label: "Cost", directionLabel: "High/Low", value: "cost desc" },
  { label: "Price", directionLabel: "Low/High", value: "price asc" },
  { label: "Price", directionLabel: "High/Low", value: "price desc" },
  { label: "Value", directionLabel: "Low/High", value: "value asc" },
  { label: "Value", directionLabel: "High/Low", value: "value desc" },
] as SortButtonChoice[];

const tabs = [
  {
    // sends collection=undiscarded param to backend
    id: "horse-variants-unarchived-tab",
    content: "Unarchived",
    panelID: "horse-variants-unarchived-tab-content",
    accessibilityLabel: "Unarchived variants",
  },
  {
    // sends collection=all param to backend
    id: "horse-variants-all-tab",
    content: "All",
    panelID: "horse-variants-all-tab-content",
    accessibilityLabel: "All variants (includes unarchived and archived variants)",
  },
];

const shopifyStatusOptions = [
  { label: "All", value: "", key: "all" },
  { label: "Draft", value: "draft", key: "draft" },
  { label: "Active", value: "active", key: "active" },
] satisfies Option[];

const shopifyStatusLabels: Record<string, string> = {
  draft: "Draft",
  active: "Active",
};

type DisambiguateLabelArgs =
  | {
      key: "horseLocations";
      value: number;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "moreThanLessThan";
      value: DecimalRangeFilter | NumberRangeFilter;
      label: string;
      prefix?: string;
      suffix?: string;
    }
  | {
      key: "productAvailability";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "productTags";
      value: string[];
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "productType";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "supplierId";
      value: number;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "vendor";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "shopifyStatus";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    };

const defaultSearchParams = new URLSearchParams();

function HorseVariantSearchFieldAndMoreFiltersComp({
  columnSelection,
  setFilters,
  selected,
  onSelect,
  initialQuantity,
  initialVendor,
  initialSupplierId,
  initialHorseLocationId,
  onClearAll,
  disableTabs = false,
  disabled = false,
  searchParams = defaultSearchParams,
  setSearchParams,
}: {
  readonly columnSelection?: ColumnSelection;
  readonly setFilters: (filters: HorseVariantsQueryParams) => void;
  readonly selected?: number;
  readonly onSelect?: (selected: number) => void;
  readonly initialQuantity?: DecimalRangeFilter;
  readonly initialVendor?: string;
  readonly initialSupplierId?: number;
  readonly initialHorseLocationId?: number;
  readonly onClearAll?: () => void;
  readonly disableTabs?: boolean;
  readonly disabled?: boolean;
  readonly searchParams?: URLSearchParams;
  readonly setSearchParams?: (searchParams: HorseVariantsQueryParams) => void;
}): React.JSX.Element {
  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const initSortedCol = getParamValue(searchParams.get("by_sort[column]"), "title");
  const initSortedColDir = getParamValue(searchParams.get("by_sort[direction]"), "asc");
  const [sort, setSort] = useState([`${initSortedCol} ${initSortedColDir}`]);
  const [collectionTitle, setCollectionTitle] = useState<string>(getParamValue(searchParams.get("collection_title")));

  // This is a single pub / unpub filter, but it's stored as an array of pub and unpub shopify channels
  //   so that multiple COULD be selected in the future.
  // Reconstruct productAvailability from URL params
  const publishedIds = getArrayParamValue<string>(
    searchParams.getAll("published_shopify_channel_ids[]"),
    "string",
    [],
  );
  const unpublishedIds = getArrayParamValue<string>(
    searchParams.getAll("unpublished_shopify_channel_ids[]"),
    "string",
    [],
  );
  const reconstructedProductAvailability = publishedIds.length > 0
    ? `${publishedIds.join("|")}:true`
    : unpublishedIds.length > 0
      ? `${unpublishedIds.join("|")}:false`
      : "";
  const [productAvailability, setProductAvailability] = useState<string>(reconstructedProductAvailability || "");

  const [cost, setCost] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_cost[more_than]")),
    less_than: getParamValue(searchParams.get("by_cost[less_than]")),
  });
  const [price, setPrice] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_price[more_than]")),
    less_than: getParamValue(searchParams.get("by_price[less_than]")),
  });
  const [productTags, setProductTags] = useState(
    getArrayParamValue<string>(searchParams.getAll("product_tags[]"), "string", []),
  );
  const [productType, setProductType] = useState(getParamValue(searchParams.get("product_type"), ""));
  const [shopifyStatus, setShopifyStatus] = useState<"draft" | "active" | "">(
    getParamValue<"draft" | "active" | "">(searchParams.get("shopify_status") || "", ""),
  );
  const [productValue, setProductValue] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_value[more_than]")),
    less_than: getParamValue(searchParams.get("by_value[less_than]")),
  });
  const [quantity, setQuantity] = useState<DecimalRangeFilter>(
    initialQuantity || {
      more_than: getParamValue(searchParams.get("by_quantity[more_than]")),
      less_than: getParamValue(searchParams.get("by_quantity[less_than]")),
    },
  );
  const [minTargetQuantity, setMinTargetQuantity] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_min[more_than]")),
    less_than: getParamValue(searchParams.get("by_min[less_than]")),
  });
  const [search, setSearch] = useState(getParamValue(searchParams.get("search"), ""));
  const [supplierId, setSupplierId] = useState<number | undefined>(
    initialSupplierId || getNumberParamValue(searchParams.get("supplier_id")),
  );
  const [horseLocationId, setHorseLocationId] = useState<number | undefined>(
    initialHorseLocationId || getNumberParamValue(searchParams.get("horse_location_id")),
  );
  const [totalVolume, setTotalVolume] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_total_volume[more_than]")),
    less_than: getParamValue(searchParams.get("by_total_volume[less_than]")),
  });
  const [vendor, setVendor] = useState<string>(initialVendor || getParamValue(searchParams.get("vendor")));
  const [weight, setWeight] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_weight[more_than]")),
    less_than: getParamValue(searchParams.get("by_weight[less_than]")),
  });
  const [mode, setMode] = useState<IndexFiltersMode>(IndexFiltersMode.Default);

  const includeDiscarded = HORSE_VARIANTS_COLLECTIONS[selected];
  const { data: vendors } = useHorseVariantVendorsOptions({ collection: includeDiscarded });
  const { data: productTypes } = useHorseVariantsProductTypeOptions({ collection: includeDiscarded });
  const { data: suppliers } = useSuppliersSearchOptions();
  const { data: shopifyChannels, isLoading: shopifyChannelsLoading } = useShopifyChannelAvailabilityOptions();
  const { data: horseLocations } = useHorseLocationsOptions();

  useEffect(() => {
    const [column, direction] = sort[0].split(" ");
    const shopifyChannelOption = shopifyChannels?.find((channel) => channel.value === productAvailability);
    const published_shopify_channel_ids: number[] = [];
    const unpublished_shopify_channel_ids: number[] = [];

    if (shopifyChannelOption) {
      const [shopifyChannelIds, isPublished] = shopifyChannelOption.value.split(":");
      if (isPublished === "true") {
        published_shopify_channel_ids.push(...shopifyChannelIds.split("|").map(softAssertNumber));
      } else if (isPublished === "false") {
        unpublished_shopify_channel_ids.push(...shopifyChannelIds.split("|").map(softAssertNumber));
      }
    }

    const queryParams: HorseVariantsQueryParams = {
      by_cost: cost,
      horse_location_id: horseLocationId,
      by_price: price,
      published_shopify_channel_ids: published_shopify_channel_ids,
      unpublished_shopify_channel_ids: unpublished_shopify_channel_ids,
      product_tags: productTags,
      product_type: productType,
      shopify_status: shopifyStatus || undefined,
      by_value: productValue,
      by_quantity: quantity,
      by_min: minTargetQuantity,
      search,
      supplier_id: supplierId,
      by_total_volume: totalVolume,
      vendor: vendor,
      by_weight: weight,
      by_sort: { column, direction },
      collection_title: collectionTitle,
    };

    const urlQueryParams: HorseVariantsQueryParams = {
      by_cost: cost,
      horse_location_id: horseLocationId,
      by_price: price,
      published_shopify_channel_ids: published_shopify_channel_ids.length > 0 ? published_shopify_channel_ids : undefined,
      unpublished_shopify_channel_ids:
        unpublished_shopify_channel_ids.length > 0 ? unpublished_shopify_channel_ids : undefined,
      product_tags: productTags.length > 0 ? productTags : undefined,
      product_type: productType,
      shopify_status: shopifyStatus || undefined,
      by_value: productValue,
      by_quantity: quantity,
      by_min: minTargetQuantity,
      search,
      supplier_id: supplierId,
      by_total_volume: totalVolume,
      vendor,
      by_weight: weight,
      by_sort: column === "title" && direction === "asc" ? undefined : { column, direction },
      collection_title: collectionTitle,
    };

    setSearchParams?.(urlQueryParams);
    setFilters(queryParams);
  }, [
    setFilters,
    setSearchParams,
    collectionTitle,
    cost,
    horseLocationId,
    price,
    productAvailability,
    productTags,
    productType,
    shopifyStatus,
    productValue,
    quantity,
    minTargetQuantity,
    search,
    shopifyChannels,
    sort,
    supplierId,
    totalVolume,
    vendor,
    weight,
  ]);

  const handleVendorChange = useCallback((value: string) => {
    setVendor(value === allVendors ? undefined : value);
  }, []);
  const handleSupplierChange = useCallback((value: string) => {
    setSupplierId(value === allSuppliers ? undefined : softAssertNumber(value));
  }, []);
  const handleProductTypeChange = useCallback((value: string) => {
    setProductType(value === allTypes ? undefined : value);
  }, []);
  const handleShopifyStatusChange = useCallback((value: string) => {
    setShopifyStatus(value as "draft" | "active" | "");
  }, []);
  const handleNewTagAddition = useCallback(
    (newTag) => {
      const payload = [...productTags, newTag];
      setProductTags(payload);
    },
    [productTags],
  );

  const handleCostRemove = useCallback(() => {
    setCost({ more_than: undefined, less_than: undefined });
  }, []);
  const handleHorseLocationRemove = useCallback(() => {
    setHorseLocationId(undefined);
  }, []);
  const handlePriceRemove = useCallback(() => {
    setPrice({ more_than: undefined, less_than: undefined });
  }, []);
  const handleProductAvailabilityRemove = useCallback(() => {
    setProductAvailability("");
  }, []);
  const handleProductTagsRemove = useCallback(() => {
    setProductTags([]);
  }, []);
  const handleProductTypeRemove = useCallback(() => {
    setProductType(undefined);
  }, []);
  const handleShopifyStatusRemove = useCallback(() => {
    setShopifyStatus("");
  }, []);
  const handleProductValueRemove = useCallback(() => {
    setProductValue({ more_than: undefined, less_than: undefined });
  }, []);
  const handleQuantityRemove = useCallback(() => {
    setQuantity({ more_than: undefined, less_than: undefined });
  }, []);
  const handleMinTargetQuantityRemove = useCallback(() => {
    setMinTargetQuantity({ more_than: undefined, less_than: undefined });
  }, []);
  const handleSearchRemove = useCallback(() => {
    setSearch("");
  }, []);
  const handleSupplierIdRemove = useCallback(() => {
    setSupplierId(undefined);
  }, []);
  const handleTotalVolumeRemove = useCallback(() => {
    setTotalVolume({ more_than: undefined, less_than: undefined });
  }, []);
  const handleVendorRemove = useCallback(() => {
    setVendor(undefined);
  }, []);
  const handleWeightRemove = useCallback(() => {
    setWeight({ more_than: undefined, less_than: undefined });
  }, []);
  const handleCollectionRemove = useCallback(() => {
    setCollectionTitle(undefined);
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    onClearAll?.();
    handleCostRemove();
    handleHorseLocationRemove();
    handlePriceRemove();
    handleProductAvailabilityRemove();
    handleProductTagsRemove();
    handleProductTypeRemove();
    handleShopifyStatusRemove();
    handleProductValueRemove();
    handleQuantityRemove();
    handleMinTargetQuantityRemove();
    handleSearchRemove();
    handleSupplierIdRemove();
    handleTotalVolumeRemove();
    handleVendorRemove();
    handleWeightRemove();
    handleCollectionRemove();
  }, [
    onClearAll,
    handleCostRemove,
    handleHorseLocationRemove,
    handlePriceRemove,
    handleProductAvailabilityRemove,
    handleProductTagsRemove,
    handleProductTypeRemove,
    handleShopifyStatusRemove,
    handleProductValueRemove,
    handleQuantityRemove,
    handleMinTargetQuantityRemove,
    handleSearchRemove,
    handleSupplierIdRemove,
    handleTotalVolumeRemove,
    handleVendorRemove,
    handleWeightRemove,
    handleCollectionRemove,
  ]);

  const disambiguateLabel = useCallback(
    ({ key, value, label = "", prefix = "", suffix = "" }: DisambiguateLabelArgs): string => {
      switch (key) {
        case "vendor":
          return `Vendor is ${value}`;
        case "supplierId":
          return `Supplier is ${suppliers.find((sup) => sup.value === value.toString())?.label || value}`;
        case "productType":
          return `Type: ${value}`;
        case "shopifyStatus":
          return `Status: ${shopifyStatusLabels[value] || value}`;
        case "productTags":
          return `Tags: ${value.join(", ")}`;
        case "productAvailability":
          return value;
        case "horseLocations":
          return `Location is ${horseLocations.find((hlo) => hlo.value === value.toString())?.label || value}`;
        case "moreThanLessThan":
          return moreThanLessThanLabel(value, label, prefix, suffix);
        default:
          throw new Error(`Unknown key: ${key as any}`);
      }
    },
    [suppliers, horseLocations],
  );

  // TODO: totalVolume
  const filters = [
    {
      key: "collection",
      label: "Collection",
      filter: <CollectionSelect collectionTitle={collectionTitle} setCollectionTitle={setCollectionTitle} />,
    },
    {
      key: "vendor",
      label: "Vendor",
      filter: (
        <Select
          label=""
          labelHidden
          onChange={handleVendorChange}
          options={vendors satisfies Option[]}
          value={vendor || allVendors}
        />
      ),
    },
    {
      key: "supplierId",
      label: "Supplier",
      filter: (
        <Select
          label=""
          labelHidden
          onChange={handleSupplierChange}
          options={suppliers satisfies Option[]}
          value={supplierId?.toString() || allSuppliers}
        />
      ),
    },
    {
      key: "productType",
      label: "Type",
      filter: (
        <Select
          label=""
          labelHidden
          onChange={handleProductTypeChange}
          options={productTypes satisfies Option[]}
          value={productType || allTypes}
        />
      ),
    },
    {
      key: "shopifyStatus",
      label: "Status",
      filter: (
        <Select
          label=""
          labelHidden
          onChange={handleShopifyStatusChange}
          options={shopifyStatusOptions}
          value={shopifyStatus}
        />
      ),
    },
    {
      key: "cost",
      label: "Cost",
      filter: (
        <MoreLessFilter
          align="right"
          less_than={cost.less_than}
          more_than={cost.more_than}
          prefix={getCurrencySymbol(userCurrency)}
          setStateCallback={setCost}
          targetState={cost}
        />
      ),
    },
    {
      key: "price",
      label: "Price",
      filter: (
        <MoreLessFilter
          align="right"
          less_than={price.less_than}
          more_than={price.more_than}
          prefix={getCurrencySymbol(userCurrency)}
          setStateCallback={setPrice}
          targetState={price}
        />
      ),
    },
    {
      key: "productValue",
      label: "Inventory value",
      filter: (
        <MoreLessFilter
          align="right"
          less_than={productValue.less_than}
          more_than={productValue.more_than}
          prefix={getCurrencySymbol(userCurrency)}
          setStateCallback={setProductValue}
          targetState={productValue}
        />
      ),
    },
    {
      key: "weight",
      label: "Weight",
      filter: (
        <MoreLessFilter
          less_than={weight.less_than}
          more_than={weight.more_than}
          setStateCallback={setWeight}
          suffix="g"
          targetState={weight}
        />
      ),
    },
    {
      key: "quantity",
      label: "Available quantity",
      filter: (
        <MoreLessFilter
          less_than={quantity.less_than}
          more_than={quantity.more_than}
          setStateCallback={setQuantity}
          targetState={quantity}
        />
      ),
    },
    {
      key: "minTargetQuantity",
      label: "Min target quantity",
      filter: (
        <MoreLessFilter
          less_than={minTargetQuantity.less_than}
          more_than={minTargetQuantity.more_than}
          setStateCallback={setMinTargetQuantity}
          suffix="pcs"
          targetState={minTargetQuantity}
          type="integer"
        />
      ),
    },
    {
      key: "productAvailability",
      label: "Publishing",
      filter: (
        <ChoiceList
          choices={shopifyChannels || []} // Fallback to empty array if shopifyChannels is undefined
          onChange={useCallback(([value]) => {
            setProductAvailability(value);
          }, [])}
          selected={[productAvailability]}
          title=""
          titleHidden
        />
      ),
    },
    {
      key: "productTags",
      label: "Tags",
      filter: (
        <TagCheckList
          createNew={false}
          onAdd={handleNewTagAddition}
          onChange={setProductTags}
          selectedOptions={productTags}
        />
      ),
    },
  ];

  const hvFilters = useMemo(() => {
    const appliedFilters: PolarisAppliedFilterInterface[] = [];

    if (!isEmpty(collectionTitle)) {
      appliedFilters.push({
        key: "collection",
        label: `Collection: ${collectionTitle}`,
        onRemove: handleCollectionRemove,
      });
    }

    if (!isEmpty(vendor)) {
      appliedFilters.push({
        key: "vendor",
        label: disambiguateLabel({ key: "vendor", value: vendor }),
        onRemove: handleVendorRemove,
      });
    }

    if (!isEmpty(supplierId)) {
      appliedFilters.push({
        key: "supplierId",
        label: disambiguateLabel({ key: "supplierId", value: supplierId }),
        onRemove: handleSupplierIdRemove,
      });
    }
    if (!isEmpty(productType)) {
      appliedFilters.push({
        key: "productType",
        label: disambiguateLabel({ key: "productType", value: productType }),
        onRemove: handleProductTypeRemove,
      });
    }
    if (!isEmpty(shopifyStatus)) {
      appliedFilters.push({
        key: "shopifyStatus",
        label: disambiguateLabel({ key: "shopifyStatus", value: shopifyStatus }),
        onRemove: handleShopifyStatusRemove,
      });
    }
    if (!isEmpty(price.more_than) || !isEmpty(price.less_than)) {
      appliedFilters.push({
        key: "price",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: price,
          label: "Price",
          prefix: getCurrencySymbol(userCurrency),
        }),
        onRemove: handlePriceRemove,
      });
    }
    if (!isEmpty(cost.more_than) || !isEmpty(cost.less_than)) {
      appliedFilters.push({
        key: "cost",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: cost,
          label: "Cost",
          prefix: getCurrencySymbol(userCurrency),
        }),
        onRemove: handleCostRemove,
      });
    }
    if (!isEmpty(productValue.more_than) || !isEmpty(productValue.less_than)) {
      appliedFilters.push({
        key: "productValue",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: productValue,
          label: "Value",
          prefix: getCurrencySymbol(userCurrency),
        }),
        onRemove: handleProductValueRemove,
      });
    }
    if (!isEmpty(weight.more_than) || !isEmpty(weight.less_than)) {
      appliedFilters.push({
        key: "weight",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: weight,
          label: "Weight",
          suffix: "g",
        }),
        onRemove: handleWeightRemove,
      });
    }
    if (!isEmpty(totalVolume.more_than) || !isEmpty(totalVolume.less_than)) {
      appliedFilters.push({
        key: "totalVolume",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: totalVolume,
          label: "Total volume",
          suffix: "m³",
        }),
        onRemove: handleTotalVolumeRemove,
      });
    }
    if (!isEmpty(quantity.more_than) || !isEmpty(quantity.less_than)) {
      appliedFilters.push({
        key: "quantity",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: quantity,
          label: "Quantity",
        }),
        onRemove: handleQuantityRemove,
      });
    }
    if (!isEmpty(minTargetQuantity.more_than) || !isEmpty(minTargetQuantity.less_than)) {
      appliedFilters.push({
        key: "minTargetQuantity",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: minTargetQuantity,
          label: "Min target quantity",
          suffix: "pcs",
        }),
        onRemove: handleMinTargetQuantityRemove,
      });
    }
    if (!isEmpty(productAvailability) && !shopifyChannelsLoading && shopifyChannels) {
      const channelOption = shopifyChannels.find((channel) => channel.value === productAvailability);
      const badgeLabel = channelOption ? channelOption.label : `Unknown (${productAvailability})`;
      appliedFilters.push({
        key: "productAvailability",
        label: disambiguateLabel({ key: "productAvailability", value: badgeLabel }),
        onRemove: handleProductAvailabilityRemove,
      });
    }
    if (!isEmpty(productTags)) {
      appliedFilters.push({
        key: "productTags",
        label: disambiguateLabel({ key: "productTags", value: productTags }),
        onRemove: handleProductTagsRemove,
      });
    }
    if (!isEmpty(horseLocationId)) {
      appliedFilters.push({
        key: "horseLocations",
        label: disambiguateLabel({ key: "horseLocations", value: horseLocationId }),
        onRemove: handleHorseLocationRemove,
      });
    }

    return appliedFilters;
  }, [
    vendor,
    supplierId,
    productType,
    shopifyStatus,
    price,
    cost,
    productValue,
    weight,
    totalVolume,
    quantity,
    minTargetQuantity,
    productAvailability,
    productTags,
    horseLocationId,
    userCurrency,
    disambiguateLabel,
    shopifyChannels,
    shopifyChannelsLoading,
    handleVendorRemove,
    handleSupplierIdRemove,
    handleProductTypeRemove,
    handleShopifyStatusRemove,
    handlePriceRemove,
    handleCostRemove,
    handleProductValueRemove,
    handleWeightRemove,
    handleTotalVolumeRemove,
    handleQuantityRemove,
    handleMinTargetQuantityRemove,
    handleProductAvailabilityRemove,
    handleProductTagsRemove,
    handleHorseLocationRemove,
    collectionTitle,
    handleCollectionRemove,
  ]);

  useEffect(() => {
    if (hvFilters.length > 0) {
      setMode(IndexFiltersMode.Filtering); // Ensure filters appear expanded when applied
    }
  }, [hvFilters]);

  return (
    <TableFilters
      appliedFilters={hvFilters}
      canCreateNewView={false}
      cancelAction={{
        onAction: () => {},
        disabled: false,
        loading: false,
      }}
      closeOnChildOverlayClick
      columnSelection={columnSelection}
      disabled={disabled}
      filters={filters}
      hideFilters={disabled}
      mode={mode}
      onClearAll={handleFiltersClearAll}
      onQueryChange={setSearch}
      onQueryClear={handleSearchRemove}
      onSelect={disableTabs ? undefined : onSelect}
      onSort={setSort}
      queryPlaceholder="Search variants"
      queryValue={search}
      selected={disableTabs ? undefined : selected}
      setMode={setMode}
      sortOptions={sortOptions}
      sortSelected={sort}
      tabs={disableTabs ? [] : tabs}
    />
  );
}

export const HorseVariantSearchFieldAndMoreFilters = React.memo(HorseVariantSearchFieldAndMoreFiltersComp);
